import { supabase } from '@/integrations/supabase/client';

export interface CrawlRequest {
  url: string;
  type: 'sitemap' | 'content' | 'robots';
}

export interface SitemapResult {
  urls: string[];
  success: boolean;
  error?: string;
}

export interface ContentResult {
  content: string;
  success: boolean;
  error?: string;
}

export interface RobotsResult {
  content: string;
  success: boolean;
  error?: string;
}

/**
 * Universal crawler service using Supabase Edge Function
 * This bypasses CORS issues and provides reliable crawling for any domain
 */
export class UniversalCrawlerService {
  
  /**
   * Fetch robots.txt from any domain
   */
  static async fetchRobotsTxt(url: string): Promise<RobotsResult> {
    try {
      console.log(`Fetching robots.txt for: ${url}`);
      
      const { data, error } = await supabase.functions.invoke('universal-crawler', {
        body: { url, type: 'robots' }
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log(`Robots.txt fetch result:`, data);
      return data;
    } catch (error) {
      console.error('Error fetching robots.txt:', error);
      return {
        content: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch sitemap URLs from any domain with intelligent discovery
   */
  static async fetchSitemap(url: string): Promise<SitemapResult> {
    try {
      console.log(`Fetching sitemap for: ${url}`);
      
      const { data, error } = await supabase.functions.invoke('universal-crawler', {
        body: { url, type: 'sitemap' }
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log(`Sitemap fetch result: Found ${data.urls?.length || 0} URLs`);
      return data;
    } catch (error) {
      console.error('Error fetching sitemap:', error);
      return {
        urls: [],
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch content from any URL
   */
  static async fetchContent(url: string): Promise<ContentResult> {
    try {
      console.log(`Fetching content for: ${url}`);
      
      const { data, error } = await supabase.functions.invoke('universal-crawler', {
        body: { url, type: 'content' }
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log(`Content fetch result: ${data.content?.length || 0} characters`);
      return data;
    } catch (error) {
      console.error('Error fetching content:', error);
      return {
        content: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate fallback URLs when sitemap is not available
   */
  static generateFallbackUrls(baseUrl: string): string[] {
    try {
      const url = new URL(baseUrl);
      const base = url.origin;
      
      // Common page patterns
      const commonPaths = [
        '/',
        '/about',
        '/about-us',
        '/contact',
        '/contact-us',
        '/services',
        '/products',
        '/blog',
        '/news',
        '/support',
        '/help',
        '/faq',
        '/pricing',
        '/team',
        '/careers',
        '/jobs',
        '/privacy',
        '/terms',
        '/legal',
        '/sitemap',
        '/search'
      ];

      return commonPaths.map(path => `${base}${path}`);
    } catch (error) {
      console.warn('Error generating fallback URLs:', error);
      return [baseUrl];
    }
  }
}